import { computed, onMounted, onUnmounted, readonly, type Ref, ref } from 'vue'
import { useRouter } from 'vue-router'
import { syncPlayer, usePlayer } from '@/composables/game/player'
import api from '@/services/api'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import {
  isActionMessage,
  isCloseMessage,
  isDoHapticMessage,
  isIframeToParentMessage,
  isReadyMessage,
  type LiveOpsInitData,
  type ParentToIframeMessage,
  ParentToIframeMessageType,
} from '@/types/live-ops-messages'
import { RouteName } from '@/routes'
import { NOTIFICATION_TYPE } from '@/types/shared/player'
import { type CurrencyAmount, type LiveOpsCurrency, type LiveOpsEventTemplate, type LiveOpsUserEvent } from '@/types/live-ops'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { useGiveaway } from '@/composables/game/giveaway'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { MYSTERY_BOX_TYPE } from '@/types/shared/player-config'
import { useLiveOpsOverlay } from '@/composables/game/live-ops-overlay'

export type LiveOpsBridgeState = {
  isLoading: Ref<boolean>
  isIframeReady: Ref<boolean>
  error: Ref<string | null>
  iframeUrl: Ref<string>
}

const pendingMineEarned = ref(0)

export function useLiveOpsBridge(eventId: string, options?: { onClose?: () => void }) {
  const router = useRouter()
  const { player, coins, goldenCoinValue, notifications } = usePlayer()
  const { availableSpins, currentBalance, updateState: updateFortuneWheelState, fortuneWheelState } = useFortuneWheel()
  const { syncUserGiveawayTickets, playerTickets } = useGiveaway()
  const { addHapticFeedback } = useHapticFeedback()
  const { closeLiveOpsOverlay } = useLiveOpsOverlay()
  const { topNumber } = useFullScreenDimensions()

  const isLoading = ref(true)
  const isIframeReady = ref(false)
  const error = ref<string | null>(null)
  const iframeRef = ref<HTMLIFrameElement | null>(null)

  // Store API data
  const liveOpsConfig = ref<LiveOpsEventTemplate | null>(null)
  const liveOpsCurrencies = ref<LiveOpsCurrency[]>([])
  const liveOpsUserEvent = ref<LiveOpsUserEvent | null>(null)
  const history: unknown[] = []

  const initData = ref<LiveOpsInitData | null>(null)

  // Payment state
  const pendingPayment = ref<{
    itemId: string
    itemName: string
    usdAmount: number
    starAmount?: number
    tonAmount?: number
    rewards: CurrencyAmount[]
    action: Record<string, unknown>
  } | null>(null)

  const iframeUrl = computed(() => {
    const baseUrl = liveOpsConfig.value?.packageUrl || ''
    if (!baseUrl) return ''
    const url = new URL(baseUrl)
    url.searchParams.set('topSpacing', String(topNumber.value))
    return url.toString()
  })
  const iframeOrigin = computed(() => {
    if (!iframeUrl.value) return ''
    try {
      const url = new URL(iframeUrl.value, window.location.origin)
      return url.origin
    } catch {
      return window.location.origin
    }
  })

  function setIframeRef(el: HTMLIFrameElement | null) {
    iframeRef.value = el
  }

  function postMessageToIframe(message: ParentToIframeMessage) {
    if (!iframeRef.value?.contentWindow || !iframeOrigin.value) {
      console.warn('[LiveOpsBridge] Cannot post message: iframe not ready')
      return
    }
    history.push({ dir: 'toIframe', message })
    // console.log(JSON.stringify(history))
    console.log('[LiveOpsBridge] Sending message:', message.type)
    iframeRef.value.contentWindow.postMessage(message, iframeOrigin.value)
  }

  function getCostCurrencies(template: LiveOpsEventTemplate): Set<string> {
    return new Set(template.costs.concat(template.rewards))
  }

  const currencyPriority: Record<string, number> = {
    dropee: 0,
    'mysterybox.kickstart': 1,
    'mysterybox.growth': 1,
    'mysterybox.expansion': 1,
    'mysterybox.executive': 1,
    'mysterybox.empire': 1,
    giveawayTicket: 2,
    spin: 4,
    goldenCoin: 5,
  }
  const defaultPriority = 3

  function orderCurrencies(currencies: LiveOpsCurrency[], template: LiveOpsEventTemplate): LiveOpsCurrency[] {
    const costSet = new Set(template.costs)
    const rewardSet = new Set(template.rewards)

    const costCurrencies: LiveOpsCurrency[] = []
    const rewardOnlyCurrencies: LiveOpsCurrency[] = []

    for (const currency of currencies) {
      if (costSet.has(currency.id)) {
        costCurrencies.push(currency)
      } else if (rewardSet.has(currency.id)) {
        rewardOnlyCurrencies.push(currency)
      }
    }

    const getPriority = (id: string) => currencyPriority[id] ?? defaultPriority

    costCurrencies.sort((a, b) => getPriority(a.id) - getPriority(b.id))
    rewardOnlyCurrencies.sort((a, b) => getPriority(a.id) - getPriority(b.id))

    return [...costCurrencies, ...rewardOnlyCurrencies]
  }

  function buildBalances(filterCurrencies?: Set<string>): Record<string, number> {
    const allBalances: Record<string, number> = {
      goldenCoin: Math.floor(coins.value / goldenCoinValue.value),
      spin: availableSpins.value ?? 0,
      giveawayTicket: playerTickets.value ?? 0,
      dropee: Math.floor(currentBalance.value),
    }

    const mysteryBoxCurrencyMap: Record<string, MYSTERY_BOX_TYPE> = {
      'mysterybox.kickstart': MYSTERY_BOX_TYPE.STARTER,
      'mysterybox.growth': MYSTERY_BOX_TYPE.POWER,
      'mysterybox.expansion': MYSTERY_BOX_TYPE.MASTER,
      'mysterybox.executive': MYSTERY_BOX_TYPE.ELITE,
      'mysterybox.empire': MYSTERY_BOX_TYPE.LEGENDARY,
    }

    for (const [currencyId, boxType] of Object.entries(mysteryBoxCurrencyMap)) {
      allBalances[currencyId] = player.value.ecommerce.mysteryBoxes?.[boxType] ?? 0
    }

    Object.entries(player.value.specialCurrencies || {}).forEach(([currencyId, amount]) => {
      allBalances[currencyId] = amount ?? 0
    })

    // If no filter provided, return all balances
    if (!filterCurrencies) {
      return allBalances
    }

    // Filter to only include currencies used in costs/rewards
    const filteredBalances: Record<string, number> = {}
    for (const currency of filterCurrencies) {
      if (currency in allBalances) {
        filteredBalances[currency] = allBalances[currency]
      }
    }
    return filteredBalances
  }

  async function fetchLiveOpsData(): Promise<boolean> {
    try {
      const response = await api.liveOpsGetUserEvents()

      // Find the user event for this eventId
      const userEvent = response.userEvents.find(ue => ue.id === eventId)
      if (!userEvent) {
        closeLiveOpsOverlay()
        if (options?.onClose) {
          options.onClose()
        } else if (router.currentRoute.value.name !== RouteName.HOME) {
          await router.replace({ name: RouteName.HOME })
        }
        return false
      }

      // Find the matching event template
      const eventTemplate = response.eventTemplates.find(t => t.id === userEvent.eventTemplateId)
      if (!eventTemplate) {
        error.value = `No event template found for templateId: ${userEvent.eventTemplateId}`
        return false
      }

      initData.value = buildInitData({
        liveOpsConfig: eventTemplate,
        liveOpsCurrencies: response.currencies,
        liveOpsUserEvent: userEvent,
      })
      liveOpsConfig.value = eventTemplate
      liveOpsCurrencies.value = response.currencies
      liveOpsUserEvent.value = userEvent

      return true
    } catch (e) {
      console.error('[LiveOpsBridge] Failed to fetch live ops data:', e)
      error.value = 'Failed to fetch event data'
      return false
    }
  }

  function buildInitData({ liveOpsConfig, liveOpsUserEvent, liveOpsCurrencies }: { liveOpsConfig: LiveOpsEventTemplate, liveOpsUserEvent: LiveOpsUserEvent, liveOpsCurrencies: LiveOpsCurrency[] }): LiveOpsInitData | null {

    const costCurrencies = getCostCurrencies(liveOpsConfig)
    const filteredCurrencies = liveOpsCurrencies.filter(c => costCurrencies.has(c.id))
    const orderedCurrencies = orderCurrencies(filteredCurrencies, liveOpsConfig)

    return {
      config: liveOpsConfig,
      currencies: orderedCurrencies,
      userEvent: liveOpsUserEvent,
      balances: buildBalances(costCurrencies),
      goldenCoinValue: goldenCoinValue.value,
    }
  }

  async function sendInitData() {
    // Fetch live ops data if not already fetched
    if (!liveOpsConfig.value || !liveOpsUserEvent.value) {
      const success = await fetchLiveOpsData()
      if (!success) return
    }

    console.log('initData: ', JSON.parse(JSON.stringify(initData)))
    if (!initData.value) {
      error.value = 'Failed to build init data'
      return
    }
    postMessageToIframe({
      type: ParentToIframeMessageType.INIT_DATA,
      payload: JSON.parse(JSON.stringify(initData.value)),
    })
  }

  async function onIframeLoad() {
    console.log('[LiveOpsBridge] Iframe loaded')
    await sendInitData()
    setTimeout(() => {
      isLoading.value = false
    }, 1000)
  }

  async function sendProgressUpdate() {
    // Re-fetch to get updated userEvent state
    await Promise.all([
      fetchLiveOpsData(),
      syncPlayer(),
      updateFortuneWheelState(),
      syncUserGiveawayTickets(),
    ])

    if (!liveOpsUserEvent.value) {
      console.warn('[LiveOpsBridge] Cannot send progress update: no user event')
      return
    }

    const costCurrencies = liveOpsConfig.value
      ? getCostCurrencies(liveOpsConfig.value)
      : undefined

    postMessageToIframe({
      type: ParentToIframeMessageType.PROGRESS_UPDATE,
      payload: JSON.parse(JSON.stringify({
        userEvent: liveOpsUserEvent.value,
        balances: buildBalances(costCurrencies),
        goldenCoinValue: goldenCoinValue.value,
      })),
    })
  }

  async function handleAction(actionPayload: Record<string, unknown>) {
    console.log('[LiveOpsBridge] Action received:', actionPayload)

    if (!liveOpsUserEvent.value) {
      console.warn('[LiveOpsBridge] Cannot handle action: no user event')
      return
    }

    try {
      const response = await api.liveOpsDoUserEventAction({
        userEventId: liveOpsUserEvent.value.id,
        action: actionPayload,
      })

      if (!response.success && response.paymentInfo) {
        const { paymentCurrencies, paymentInfo } = response
        pendingPayment.value = {
          ...paymentInfo,
          starAmount: Math.ceil(paymentInfo.usdAmount / Number(paymentCurrencies.star)),
          tonAmount: Math.round(90 * paymentInfo.usdAmount / Number(paymentCurrencies.ton)) / 100,
          action: actionPayload,
        }
        return
      }

      const specialCurrenciesBefore = { ...player.value.specialCurrencies }
      await syncPlayer()
      const mineBefore = specialCurrenciesBefore['mp.mine'] ?? 0
      const mineAfter = (player.value.specialCurrencies as Record<string, number>)['mp.mine'] ?? 0
      if (mineAfter > mineBefore) {
        pendingMineEarned.value += mineAfter - mineBefore
      }
      await sendProgressUpdate()
      const now = Date.now()
      for (const [key, count] of Object.entries(player.value.specialCurrencies)) {
        if (key.startsWith('badge.') && (count ?? 0) > (specialCurrenciesBefore[key] ?? 0)) {
          notifications.value.push({
            type: NOTIFICATION_TYPE.BADGE,
            timestamp: now,
            badgeId: key.slice('badge.'.length),
          })
        }
      }
    } catch (e) {
      console.error('[LiveOpsBridge] Action failed:', e)
    }
  }

  async function handlePaymentComplete() {
    const specialCurrenciesBefore = { ...player.value.specialCurrencies }
    pendingPayment.value = null
    await syncPlayer()
    const mineBefore = specialCurrenciesBefore['mp.mine'] ?? 0
    const mineAfter = (player.value.specialCurrencies as Record<string, number>)['mp.mine'] ?? 0
    if (mineAfter > mineBefore) {
      pendingMineEarned.value += mineAfter - mineBefore
    }
    await sendProgressUpdate()
  }

  function handlePaymentCancel() {
    if (pendingPayment.value) {
      postMessageToIframe({
        type: ParentToIframeMessageType.PAYMENT_CANCELLED,
        payload: JSON.parse(JSON.stringify({
          action: pendingPayment.value.action,
        })),
      })
    }
    pendingPayment.value = null
  }

  function handleMessage(event: MessageEvent) {
    if (!iframeOrigin.value || event.origin !== iframeOrigin.value) {
      return
    }

    if (!isIframeToParentMessage(event.data)) {
      console.warn('[LiveOpsBridge] Invalid message format:', event.data)
      return
    }

    const message = event.data
    history.push({ dir: 'fromIframe', message })
    // console.log(JSON.stringify(history))
    console.log('[LiveOpsBridge] Received message:', message.type)

    if (isReadyMessage(message)) {
      isIframeReady.value = true
      sendInitData()
    } else if (isActionMessage(message)) {
      handleAction(message.payload)
    } else if (isDoHapticMessage(message)) {
      addHapticFeedback(message.payload?.style)
    } else if (isCloseMessage(message)) {
      console.log('[LiveOpsBridge] Received close message:', message)
      addHapticFeedback('light')
      closeLiveOpsOverlay()
      if (options?.onClose) {
        options.onClose()
      } else if (router.currentRoute.value.name !== RouteName.HOME) {
        router.push({ name: RouteName.HOME })
      }
    }
  }

  function validateConfig(): boolean {
    if (!iframeUrl.value) {
      error.value = 'Live ops iframe URL not configured'
      return false
    }

    return true
  }

  onMounted(async () => {
    // Fetch live ops data first to get the packageUrl for the iframe
    const success = await fetchLiveOpsData()
    if (!success) {
      isLoading.value = false
      return
    }

    if (!validateConfig()) {
      isLoading.value = false
      return
    }

    window.addEventListener('message', handleMessage)
  })

  onUnmounted(() => {
    window.removeEventListener('message', handleMessage)
  })

  return {
    isLoading,
    isIframeReady,
    error,
    iframeUrl,
    setIframeRef,
    onIframeLoad,
    pendingPayment: readonly(pendingPayment),
    handlePaymentComplete,
    handlePaymentCancel,
    liveOpsUserEvent: readonly(liveOpsUserEvent),
    pendingMineEarned,
  }
}
